import { STORED_VERSION, type StoredFormula, type StoredNode } from './stored';
import { upgradeStoredFormula } from './migrateV2';

/**
 * How a per-cell override is written to and read from `cell_overrides.value`.
 *
 * Extracted from `Grid/cellOverrides.ts` so the Worker can decode overrides too:
 * that module also holds the queries, and importing it would pull the browser's
 * Supabase client — and its `import.meta.env` — into the Worker bundle. The
 * decoding itself is pure, and there must be exactly one of it: a second reader
 * of this jsonb would eventually disagree about what a stored formula is.
 */

export type CellOverrideState =
  | { kind: 'none' }
  | { kind: 'value'; value: number | string }
  | { kind: 'blank' }
  | { kind: 'formula'; formula: StoredFormula };

/** Database representation of a persistent per-cell formula. Keep this small and
 * explicitly versioned: `cell_overrides.value` also stores scalar manual values. */
export interface CellFormulaEnvelope {
  readonly type: 'formula';
  readonly version: 3;
  readonly ast: StoredNode;
}

export const NO_OVERRIDE: CellOverrideState = { kind: 'none' };

export function formulaEnvelope(formula: StoredFormula): CellFormulaEnvelope {
  return { type: 'formula', version: STORED_VERSION, ast: formula.ast };
}

/** The stored formula inside an override envelope, or `undefined` when the value is not one.
 *  A v2 envelope (`{ version: 2, canonical }`) is upgraded here, the same way column formulas
 *  are upgraded in `normaliseGridFormulas`. */
function envelopeFormula(value: unknown): StoredFormula | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const candidate = value as Record<string, unknown>;
  if (candidate.type !== 'formula') return undefined;
  if (candidate.version === STORED_VERSION) return { v: STORED_VERSION, ast: candidate.ast as StoredNode };
  if (candidate.version === 2) return upgradeStoredFormula(candidate.canonical);
  return undefined;
}

/** Decode untrusted jsonb without ever treating a future/malformed object as a
 * printable manual value. `undefined` means the row should be ignored. */
export function decodeOverrideValue(value: unknown): CellOverrideState | undefined {
  if (value === null) return { kind: 'blank' };
  if (typeof value === 'number' && Number.isFinite(value)) return { kind: 'value', value };
  if (typeof value === 'string') return { kind: 'value', value };
  const formula = envelopeFormula(value);
  if (formula !== undefined) return { kind: 'formula', formula };
  return undefined;
}

/** Map key: one map per grid, keyed by `field|rowKey`. */
export const overrideKey = (field: string, rowKey: string): string =>
  `${field}|${rowKey}`;
